import AppLoader from "../components/AppLoader";
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Fade,
  Grow,
  useTheme,
} from "@mui/material";
import AppBackground from "../components/layout/AppBackground";
import { useTranslation } from "react-i18next";

const Faqs = () => {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("faqs");
  const theme = useTheme();

  const [expandedGroup, setExpandedGroup] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    setLoading(false);
  }, []);

  const faqGroups = [
    {
      title: t("faq_group1"),
      items: [3, 4, 5],
    },
    {
      title: t("faq_group2"),
      items: [2, 6, 7, 10],
    },
    {
      title: t("faq_group3"),
      items: [1, 8, 9, 11, 13],
    },
    {
      title: t("faq_group4"),
      items: [14, 15, 16, 12],
    },
  ];

  const handleGroupChange = (groupIndex) => {
    setExpandedGroup((prev) => (prev === groupIndex ? null : groupIndex));
  };

  const handleItemChange = (groupIndex, itemIndex) => {
    setExpandedItems((prev) => ({
      ...prev,
      [groupIndex]: prev[groupIndex] === itemIndex ? null : itemIndex,
    }));
  };

  if (loading) return <AppLoader message={t("loading")} />;

  return (
    <AppBackground
      sx={{
        minHeight: "100vh",
        py: { xs: 5, md: 8 },
        px: 0.5,
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <Fade in timeout={700}>
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "860px",
            mt: 0,
            mb: { xs: 4, md: 6 },
            py: 0,
            color: "#fff",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="overline"
              sx={{
                display: "inline-flex",
                width: "fit-content",
                px: 1.5,
                py: 0.05,
                borderRadius: "999px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#e9dafd",
                fontSize: "0.82rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              {t("faq_kicker")}
            </Typography>

          <Typography variant="h4" gutterBottom sx={{ color: "white" }}>

              {t("faq_title")}
            </Typography>
          </Box>

          <Grow in timeout={800}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                pb: { xs: 2, md: 3 },
              }}
            >
              {faqGroups.map((group, groupIndex) => (
                <Accordion
                  key={groupIndex}
                  expanded={expandedGroup === groupIndex}
                  onChange={() => handleGroupChange(groupIndex)}
                  disableGutters
                  square={false}
                  sx={{
                    background: "rgba(18, 14, 28, 0.72)",
                    border:
                      expandedGroup === groupIndex
                        ? `1px solid ${theme.palette.secondary.main}55`
                        : "1px solid rgba(255,255,255,0.08)",

                    borderRadius: "22px !important",
                    overflow: "hidden",
                    margin: 0,

                    "&:before": {
                      display: "none",
                    },

                    "&.Mui-expanded": {
                      margin: "0 !important",
                      borderRadius: "22px !important",
                    },

                    "&.MuiAccordion-root:first-of-type": {
                      borderTopLeftRadius: "22px !important",
                      borderTopRightRadius: "22px !important",
                      borderBottomLeftRadius: "22px !important",
                      borderBottomRightRadius: "22px !important",
                    },

                    "&.MuiAccordion-root:last-of-type": {
                      borderTopLeftRadius: "22px !important",
                      borderTopRightRadius: "22px !important",
                      borderBottomLeftRadius: "22px !important",
                      borderBottomRightRadius: "22px !important",
                    },

                    "&.MuiAccordion-root.Mui-expanded:first-of-type": {
                      borderTopLeftRadius: "22px !important",
                      borderTopRightRadius: "22px !important",
                      borderBottomLeftRadius: "22px !important",
                      borderBottomRightRadius: "22px !important",
                    },

                    "&.MuiAccordion-root.Mui-expanded:last-of-type": {
                      borderTopLeftRadius: "22px !important",
                      borderTopRightRadius: "22px !important",
                      borderBottomLeftRadius: "22px !important",
                      borderBottomRightRadius: "22px !important",
                    },

                    boxShadow:
                      expandedGroup === groupIndex
                        ? "0 30px 80px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(171, 71, 188, 0.08)"
                        : "0 30px 80px rgba(0, 0, 0, 0.35)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    transition: "border-color 0.25s ease, box-shadow 0.25s ease",

                    "&:hover": {
                      borderColor:
                        expandedGroup === groupIndex
                          ? `${theme.palette.secondary.main}66`
                          : "rgba(255,255,255,0.14)",
                    },
                  }}
                  
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRight: "2px solid rgba(255,255,255,0.72)",
                          borderBottom: "2px solid rgba(255,255,255,0.72)",
                          transform:
                            expandedGroup === groupIndex
                              ? "rotate(-135deg)"
                              : "rotate(45deg)",
                          transition: "transform 0.25s ease",
                          mr: 0.5,
                          mb: expandedGroup === groupIndex ? "-2px" : "2px",
                        }}
                      />
                    }
                    sx={{
                      px: 2.5,
                      py: 0.5,
                      minHeight: "unset",
                      borderRadius: "22px",

                      "&.Mui-expanded": {
                        minHeight: "unset",
                      },

                      "& .MuiAccordionSummary-content": {
                        my: 1.4,
                      },

                      "& .MuiAccordionSummary-content.Mui-expanded": {
                        my: 1.4,
                      },

                      "& .MuiAccordionSummary-expandIconWrapper": {
                        transform: "none !important",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.08rem",
                        letterSpacing: "-0.01em",
color:
  expandedGroup === groupIndex
    ? "rgba(185, 128, 255, 0.45)"
    : "rgba(185, 128, 255, 0.75)",
                        transition: "color 0.2s ease",
                      }}
                    >
                      {group.title}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      px: 2.5,
                      pt: 0.25,
                      pb: 2,
                    }}
                  >
                    {group.items.map((faqNumber, itemIndex) => {
                      const isOpen = expandedItems[groupIndex] === itemIndex;

                      return (
                        <Box
                          key={faqNumber}
                          sx={{
                            borderBottom:
                              itemIndex === group.items.length - 1
                                ? "none"
                                : "1px solid rgba(255,255,255,0.08)",
                            py: 1.5,
                          }}
                        >
                          <Box
                            onClick={() => handleItemChange(groupIndex, itemIndex)}
                            sx={{
                              cursor: "pointer",
                              position: "relative",
                              pr: 4,
                              transition: "opacity 0.2s ease",
                              "&:hover": {
                                opacity: 0.96,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.84)",
                                fontSize: "0.98rem",
                                fontWeight: 600,
                                lineHeight: 1.5,
                                pr: 2,
                              }}
                            >
                              {t(`faq_q${faqNumber}`)}
                            </Typography>

                            <Box
                              sx={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                width: 24,
                                textAlign: "center",
                                color: isOpen
                                  ? theme.palette.secondary.main
                                  : "rgba(255,255,255,0.62)",
                                fontSize: "1.3rem",
                                fontWeight: 400,
                                lineHeight: 1,
                                userSelect: "none",
                                transition: "color 0.2s ease",
                              }}
                            >
                              {isOpen ? "-" : "+"}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              maxHeight: isOpen ? "500px" : "0px",
                              overflow: "hidden",
                              transition:
                                "max-height 0.32s ease, padding-top 0.32s ease, padding-bottom 0.32s ease",
                              pt: isOpen ? "10px" : 0,
                              pb: isOpen ? "8px" : 0,
                            }}
                          >
                            <Typography
                              sx={{
                                color: "rgba(240,240,240,0.84)",
                                lineHeight: 1.8,
                                fontSize: { xs: "0.98rem", sm: "1rem" },
                              }}
                            >
                              {t(`faq_a${faqNumber}`)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Grow>
        </Container>
      </Fade>
    </AppBackground>
  );
};

export default Faqs;